import type { Board, Colour, Move } from './types';
import { other } from './board';

const ORTH = [[1, 0], [-1, 0], [0, 1], [0, -1]] as const;
const DIAG = [[1, 1], [1, -1], [-1, 1], [-1, -1]] as const;
const KNIGHT = [[2, 1], [2, -1], [-2, 1], [-2, -1], [1, 2], [1, -2], [-1, 2], [-1, -2]] as const;

/** Rook, bishop and queen: at most one of these moves per turn. */
export const HEAVY = new Set(['R', 'B', 'Q']);

/** The options that change which moves are legal. */
export interface MoveRules {
  pawnRebuild: boolean;
  /** The knight joins the heavy pieces. */
  knightHeavy: boolean;
}
export const BASE_RULES: MoveRules = { pawnRebuild: false, knightHeavy: false };

export const isHeavy = (t: string, knightHeavy: boolean) => HEAVY.has(t) || (knightHeavy && t === 'N');

const inBounds = (r: number, f: number) => r >= 0 && r < 8 && f >= 0 && f < 8;

/** Row of a colour's pawn start rank (rank 2 for light, rank 7 for dark). */
export const pawnStartRow = (c: Colour) => (c === 'l' ? 6 : 1);
const pawnDir = (c: Colour) => (c === 'l' ? -1 : 1);

/** Squares one normal chess move can reach from `i`, ignoring who controls the piece. */
export function pieceMoves(board: Board, i: number): number[] {
  const p = board[i]!;
  const r = (i / 8) | 0;
  const f = i % 8;
  const out: number[] = [];
  const slide = (dirs: readonly (readonly [number, number])[], max: number) => {
    for (const [dr, df] of dirs) {
      for (let k = 1; k <= max; k++) {
        const rr = r + dr * k;
        const ff = f + df * k;
        if (!inBounds(rr, ff)) break;
        const t = board[rr * 8 + ff];
        if (!t) out.push(rr * 8 + ff);
        else {
          if (t.c !== p.c) out.push(rr * 8 + ff);
          break;
        }
      }
    }
  };
  if (p.t === 'R') slide(ORTH, 7);
  else if (p.t === 'B') slide(DIAG, 7);
  else if (p.t === 'Q') slide([...ORTH, ...DIAG], 7);
  else if (p.t === 'K') slide([...ORTH, ...DIAG], 1);
  else if (p.t === 'N') {
    for (const [dr, df] of KNIGHT) {
      const rr = r + dr;
      const ff = f + df;
      if (!inBounds(rr, ff)) continue;
      const t = board[rr * 8 + ff];
      if (!t || t.c !== p.c) out.push(rr * 8 + ff);
    }
  } else {
    const dir = pawnDir(p.c);
    const one = r + dir;
    if (inBounds(one, f) && !board[one * 8 + f]) {
      out.push(one * 8 + f);
      if (r === pawnStartRow(p.c) && !board[(r + 2 * dir) * 8 + f]) out.push((r + 2 * dir) * 8 + f);
    }
    for (const df of [-1, 1]) {
      if (!inBounds(one, f + df)) continue;
      const t = board[one * 8 + f + df];
      if (t && t.c !== p.c) out.push(one * 8 + f + df);
    }
  }
  return out;
}

/** Is `square` attacked by any piece of colour `by`? */
export function attacked(board: Board, square: number, by: Colour): boolean {
  const r = (square / 8) | 0;
  const f = square % 8;
  for (let i = 0; i < 64; i++) {
    const p = board[i];
    if (!p || p.c !== by) continue;
    if (p.t === 'P') {
      const pr = (i / 8) | 0;
      if (pr + pawnDir(p.c) === r && Math.abs((i % 8) - f) === 1) return true;
    } else if (pieceMoves(board, i).includes(square)) return true;
  }
  return false;
}

/** What the legal-move generator needs to know about the turn in progress. */
export interface TurnState {
  board: Board;
  movesLeft: number;
  heavyUsed: boolean;
  movedKings: Record<Colour, boolean>;
  /** The number rolled this turn, 0 if none. */
  rolled: number;
}

/** A king that moved this turn may not finish the turn on an attacked square. */
export function kingsSafe(board: Board, movedKings: Record<Colour, boolean>): boolean {
  for (const c of ['l', 'd'] as const) {
    if (!movedKings[c]) continue;
    const k = board.findIndex((p) => p && p.t === 'K' && p.c === c);
    if (k >= 0 && attacked(board, k, other(c))) return false;
  }
  return true;
}

export interface Applied extends TurnState {
  captured: Board[number];
}

export function applyMove(s: TurnState, m: Move, colour: Colour, knightHeavy = false): Applied {
  const board = s.board.slice();
  const movedKings = { ...s.movedKings };
  if (m.kind === 'add') {
    board[m.to] = { t: 'P', c: colour };
    return { board, movesLeft: s.movesLeft - 1, heavyUsed: s.heavyUsed, movedKings, rolled: s.rolled, captured: null };
  }
  const p = board[m.from]!;
  const captured = board[m.to] ?? null;
  board[m.to] = p;
  board[m.from] = null;
  if (p.t === 'K') movedKings[p.c] = true;
  return {
    board,
    movesLeft: s.movesLeft - 1,
    heavyUsed: s.heavyUsed || isHeavy(p.t, knightHeavy),
    movedKings,
    rolled: s.rolled,
    captured,
  };
}

/**
 * Every move the player controlling `colour` may make now. A move is legal if
 * the turn can still be finished lawfully afterwards: capturing a king ends the
 * game, otherwise the moved kings must be safe, either now or after a further
 * legal move this turn.
 */
export function legalMoves(s: TurnState, colour: Colour, rules: MoveRules): Move[] {
  if (s.movesLeft <= 0) return [];
  const out: Move[] = [];
  const ok = (n: Applied) =>
    (n.captured?.t === 'K') ||
    kingsSafe(n.board, n.movedKings) ||
    (n.movesLeft > 0 && legalMoves(n, colour, rules).length > 0);
  for (let i = 0; i < 64; i++) {
    const p = s.board[i];
    if (!p || p.c !== colour || (isHeavy(p.t, rules.knightHeavy) && s.heavyUsed)) continue;
    for (const to of pieceMoves(s.board, i)) {
      const m: Move = { kind: 'move', from: i, to };
      if (ok(applyMove(s, m, colour, rules.knightHeavy))) out.push(m);
    }
  }
  if (rules.pawnRebuild && s.rolled >= 2 && !s.board.some((p) => p && p.t === 'P' && p.c === colour)) {
    const row = pawnStartRow(colour);
    for (let f = 0; f < 8; f++) {
      const to = row * 8 + f;
      if (s.board[to]) continue;
      const m: Move = { kind: 'add', to };
      const n = applyMove(s, m, colour, rules.knightHeavy);
      if (kingsSafe(n.board, n.movedKings) || (n.movesLeft > 0 && legalMoves(n, colour, rules).length > 0)) out.push(m);
    }
  }
  return out;
}
